import React, { useState, useEffect } from 'react';
import { IsomPart, DrawingElement } from '@/app/types/cad';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { ScrollArea } from '@/app/components/ui/scroll-area';
import { Input } from '@/app/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/app/components/ui/dialog';
import { Trash2, Download, Upload, Search } from 'lucide-react';
import { toast } from 'sonner';

import { saveFileWithDialog } from '@/app/utils/projectIO';

interface PartsLibraryProps {
  open: boolean;
  onClose: () => void;
  onLoadPart: (elements: DrawingElement[], name: string) => void;
  currentElements: DrawingElement[];
  currentPartName: string;
}

export function PartsLibrary({
  open,
  onClose,
  onLoadPart,
  currentElements,
  currentPartName,
}: PartsLibraryProps) {
  const [parts, setParts] = useState<IsomPart[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Load parts from localStorage on mount
  useEffect(() => {
    const savedParts = localStorage.getItem('isomparts-library');
    if (savedParts) {
      try {
        setParts(JSON.parse(savedParts));
      } catch (error) {
        console.error('Error loading parts library:', error);
      }
    }
  }, [open]);

  // Save parts to localStorage
  const saveParts = (newParts: IsomPart[]) => {
    localStorage.setItem('isomparts-library', JSON.stringify(newParts));
    setParts(newParts);
  };

  // Save current part to library
  const handleSaveTolibrary = () => {
    const name = prompt('Name für das Bauteil in der Bibliothek:', currentPartName);
    if (!name) return;

    const newPart: IsomPart = {
      name,
      version: '1.0',
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
      elements: currentElements,
    };

    const existingIndex = parts.findIndex((p) => p.name === name);
    let newParts: IsomPart[];
    
    if (existingIndex >= 0) {
      if (!window.confirm(`Bauteil "${name}" existiert bereits. Überschreiben?`)) {
        return;
      }
      newParts = [...parts];
      newParts[existingIndex] = { ...newPart, created: parts[existingIndex].created };
    } else {
      newParts = [...parts, newPart];
    }

    saveParts(newParts);
    toast.success(`Bauteil "${name}" zur Bibliothek hinzugefügt`);
  };

  // Load part from library
  const handleLoadFromLibrary = (part: IsomPart) => {
    onLoadPart(part.elements, part.name);
    onClose();
    toast.success(`Bauteil "${part.name}" geladen`);
  };

  // Delete part from library
  const handleDelete = (name: string) => {
    if (!window.confirm(`Bauteil "${name}" aus der Bibliothek löschen?`)) {
      return;
    }

    const newParts = parts.filter((p) => p.name !== name);
    saveParts(newParts);
    toast.success(`Bauteil "${name}" gelöscht`);
  };

  // Export part to file
  const handleExport = async (part: IsomPart) => {
    const blob = new Blob([JSON.stringify(part, null, 2)], {
      type: 'application/json',
    });
    
    await saveFileWithDialog(
      blob,
      `${part.name.replace(/\s+/g, '_')}.isompart`,
      'IsoBlock Bauteil',
      'isompart',
      'application/json'
    );
    
    toast.success(`Bauteil "${part.name}" exportiert`);
  };

  // Import part from file
  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.isompart';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const part: IsomPart = JSON.parse(event.target?.result as string);
          
          const existingIndex = parts.findIndex((p) => p.name === part.name);
          let newParts: IsomPart[];
          
          if (existingIndex >= 0) {
            if (!window.confirm(`Bauteil "${part.name}" existiert bereits. Überschreiben?`)) {
              return;
            }
            newParts = [...parts];
            newParts[existingIndex] = part;
          } else {
            newParts = [...parts, part];
          }

          saveParts(newParts);
          toast.success(`Bauteil "${part.name}" importiert`);
        } catch (error) {
          toast.error('Fehler beim Importieren der Datei');
          console.error(error);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const filteredParts = parts.filter((part) =>
    part.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Bauteil-Bibliothek</DialogTitle>
          <DialogDescription>
            Speichern, laden, exportieren und importieren Sie Bauteile.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Actions */}
          <div className="flex gap-2">
            <Button onClick={handleSaveTolibrary} size="sm" className="flex-1">
              <Upload className="h-4 w-4 mr-2" />
              Aktuelles Bauteil speichern
            </Button>
            <Button onClick={handleImport} size="sm" variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Importieren
            </Button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Bauteile suchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Parts list */}
          <ScrollArea className="h-[400px] border rounded-lg">
            <div className="p-4 space-y-2">
              {filteredParts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchQuery ? 'Keine Bauteile gefunden' : 'Bibliothek ist leer'}
                </div>
              ) : (
                filteredParts.map((part) => (
                  <Card key={part.name} className="hover:bg-gray-50 transition-colors">
                    <CardHeader className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-sm font-medium">
                            {part.name}
                          </CardTitle>
                          <div className="text-xs text-gray-500 mt-1">
                            {part.elements.length} Elemente · Erstellt:{' '}
                            {new Date(part.created).toLocaleDateString('de-DE')}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleLoadFromLibrary(part)}
                          >
                            Laden
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleExport(part)}
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(part.name)}
                          >
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}